// @ts-check
/**
 * SHIP-05 (PW-03): the git status of the chosen workspace, read from one
 * bounded server endpoint (`git status --porcelain=v2 --branch`), shown as a
 * compact chip in the composer and as a line on the repository page.
 */
import { h } from "./ui/dom.js";

/**
 * @typedef {{
 *   available: boolean, reason: string | null, branch: string | null,
 *   detached: boolean, head: string | null, upstream: string | null,
 *   ahead: number | null, behind: number | null, dirty: number,
 *   untracked: number, conflicted: number, diverged: boolean, truncated: boolean,
 * }} BranchStatus
 */

/** @type {Record<string, string>} */
const reasons = {
  workspace_not_repository: "This workspace isn't a Git checkout.",
  workspace_unavailable: "The workspace is still being prepared.",
  git_status_unavailable: "Git status isn't available right now.",
  git_status_timeout: "Git status took too long.",
};

/**
 * Words for one status: a short chip text, a tone and a full sentence.
 * @param {BranchStatus} status
 * @returns {{ text: string, tone: "neutral" | "working" | "waiting" | "failed", state: string, detail: string }}
 */
export function describeGitStatus(status) {
  if (!status.available)
    return {
      text: "No Git status",
      tone: "neutral",
      state: "unavailable",
      detail: reasons[status.reason ?? ""] ?? "Git status isn't available.",
    };
  const name = status.detached
    ? `detached ${status.head?.slice(0, 7) ?? ""}`.trim()
    : (status.branch ?? "unknown branch");
  const changed = status.dirty + status.untracked + status.conflicted;
  const parts = [name];
  if (status.ahead) parts.push(`↑${status.ahead}`);
  if (status.behind) parts.push(`↓${status.behind}`);
  const sentences = [
    status.detached
      ? `Detached at ${status.head?.slice(0, 12) ?? "an unknown commit"}.`
      : `On ${name}${status.upstream ? `, tracking ${status.upstream}` : ", with no upstream"}.`,
  ];
  let state = "clean";
  let tone = /** @type {"neutral" | "working" | "waiting" | "failed"} */ (
    "working"
  );
  if (status.diverged) {
    parts.push("diverged");
    state = "diverged";
    tone = "failed";
    sentences.push(
      `Diverged: ${status.ahead} ahead and ${status.behind} behind the remote branch.`,
    );
  } else if (status.ahead) {
    state = "ahead";
    tone = "waiting";
    sentences.push(
      `${status.ahead} commit${status.ahead === 1 ? "" : "s"} ahead of the remote branch.`,
    );
  } else if (status.behind) {
    state = "behind";
    tone = "waiting";
    sentences.push(
      `${status.behind} commit${status.behind === 1 ? "" : "s"} behind the remote branch.`,
    );
  }
  if (changed) {
    parts.push(`${changed} changed`);
    if (state === "clean") {
      state = "dirty";
      tone = "waiting";
    }
    sentences.push(
      `${status.dirty} modified, ${status.untracked} untracked${status.conflicted ? `, ${status.conflicted} in conflict` : ""}${status.truncated ? " (counts are partial)" : ""}.`,
    );
  } else if (state === "clean") {
    parts.push("clean");
    sentences.push("No uncommitted changes.");
  }
  if (status.conflicted) tone = "failed";
  return { text: parts.join(" · "), tone, state, detail: sentences.join(" ") };
}

/**
 * The chip itself. It stays hidden until a workspace with a readable status is
 * chosen, so a bunny workspace that is not a repository adds no noise.
 * @param {{ client: any }} options
 */
export function createGitStatusChip({ client }) {
  const element = h("span", {
    class: "composer-git-status",
    role: "status",
    hidden: true,
  });
  let sequence = 0;
  /** @param {BranchStatus | undefined} status */
  function render(status) {
    if (!status?.available) {
      element.hidden = true;
      element.textContent = "";
      return;
    }
    const words = describeGitStatus(status);
    element.hidden = false;
    element.textContent = words.text;
    element.title = words.detail;
    element.dataset.state = words.state;
    element.dataset.tone = words.tone;
    element.setAttribute("aria-label", `Git status: ${words.detail}`);
  }
  return {
    element,
    render,
    /** @param {string | undefined} tenantId @param {string | undefined} workspaceId */
    async show(tenantId, workspaceId) {
      const current = ++sequence;
      if (!tenantId || !workspaceId || !client.workspaceGitStatus) {
        render(undefined);
        return;
      }
      try {
        const status = await client.workspaceGitStatus({
          tenantId,
          workspaceId,
        });
        if (current === sequence) render(status);
      } catch {
        // The chip is a hint: when the status can't be read it simply hides.
        if (current === sequence) render(undefined);
      }
    },
  };
}
